import { type KeyboardEvent } from 'react';
import { Editable, useSlate } from 'slate-react';
import type { EditableProps } from 'slate-react/dist/components/editable';
import type { Editor as SlateEditor } from 'slate';
import { useRefs } from '@halo/hooks';
import { useEditorContext } from './scripts/context';
import classes from './editor.module.scss';

export interface EditorProps extends Omit<EditableProps, 'onChange'> {
  onKeyDown?: (event: KeyboardEvent<HTMLDivElement>) => void;
  onChange?: (editor: SlateEditor) => void;
}

export function Editor({
  className,
  children,
  onChange,
  onKeyDown: onKeyDown_user,
  ...props
}: EditorProps) {
  const {
    editableProps: { editableRef, onKeyDown: onKeyDown_context, ...editableProps },
  } = useEditorContext();

  const editor = useSlate();

  const onKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      onKeyDown_context?.(event);
      if (!event.defaultPrevented) {
        onKeyDown_user?.(event);
      }
    },
    [onKeyDown_user, onKeyDown_context]
  );

  const refs = useRefs({ onChange, editor });

  useLayoutEffect(() => {
    const { onChange, editor } = refs.current;
    onChange?.(editor);
  }, [refs, editor.children]);

  return (
    <Editable
      ref={editableRef}
      className={cls(className, classes.editor, 'slate-editor')}
      onKeyDown={onKeyDown}
      disableDefaultStyles={true}
      {...editableProps}
      {...props}
    />
  );
}
